import React, { useMemo, useEffect } from 'react';
import { DataLoadingSpinner } from '@/components/UI/DataLoadingSpinner';
import { trpcPostUnbatched } from '@/api/trpc';
import { useQuery } from '@tanstack/react-query';
import { buildParameterTableRow } from './helpers';
import type { ParameterTableRow } from './types';
import type { ObjectiveMode } from './ObjectiveModeControl';

interface ParameterAnalysisDetailsPanelProps {
    duration: string;
    experimentId: number;
    horizonSeconds: number;
    metric: 'stock' | 'options';
    objectiveMode: ObjectiveMode;
    realizedOutcomeSetId: number | null;
    onDetailsLoaded: (row: ParameterTableRow) => void;
    parameterRow: ParameterTableRow;
    renderContent: (row: ParameterTableRow) => React.ReactNode;
}

const ParameterAnalysisDetailsPanel = ({
    duration,
    experimentId,
    horizonSeconds,
    metric,
    objectiveMode,
    realizedOutcomeSetId,
    onDetailsLoaded,
    parameterRow,
    renderContent
}: ParameterAnalysisDetailsPanelProps) => {
    const detailsQuery = useQuery({
        ...trpcPostUnbatched.experiment.getParameterAnalysisDetails.queryOptions({
            experimentId,
            duration,
            horizonSeconds,
            metric,
            objectiveMode,
            realizedOutcomeSetId,
            parameterPath: parameterRow.path,
            featureId: parameterRow.preferenceIdentity.featureId ?? parameterRow.canonicalFeature?.featureId,
            featureKey: parameterRow.preferenceIdentity.featureKey ?? parameterRow.canonicalFeature?.featureKey,
            contextFeatureRef:
                parameterRow.preferenceIdentity.contextFeatureRef ?? parameterRow.canonicalFeature?.serializedContextFeatureRef
        }),
        staleTime: 300000,
        refetchOnWindowFocus: false
    });

    const detailRow = useMemo(() => {
        const details = detailsQuery.data;
        return details ? buildParameterTableRow(details.parameterPath, details) : null;
    }, [detailsQuery.data]);

    useEffect(() => {
        if (detailRow) onDetailsLoaded(detailRow);
    }, [detailRow, onDetailsLoaded]);

    if (detailsQuery.isLoading) {
        return (
            <div className="p-6">
                <DataLoadingSpinner text="Loading value breakdown..." />
            </div>
        );
    }

    if (detailsQuery.error) {
        return <div className="p-4 text-red-400">Error loading value breakdown: {detailsQuery.error.message}</div>;
    }

    if (!detailRow) {
        return <div className="p-4 text-zinc-400">No value breakdown is available for this parameter.</div>;
    }

    return <>{renderContent(detailRow)}</>;
};

export default ParameterAnalysisDetailsPanel;
